import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react"

import { useTranslation } from "react-i18next"

import { useToast } from "@/shared/hooks/useToast"
import { useEventCallback } from "@/shared/lib/hooks"

import {
  cloudStorageController,
  getStorageErrorMessage,
  STORAGE_ERROR_CODE
} from "../../controllers/cloud-storage-controller"

import type { ResolvedStorageError, StorageOperationCallbacks } from "../../controllers/cloud-storage-controller"

interface UseTelegramCloudStorageParams<T> {
  /** Storage key (1-128 characters, A-Za-z0-9_-). */
  key: string
  /** Synchronous fallback returned until the controller has settled the key. */
  defaultValue: T
}

export interface UseTelegramCloudStorageResult<T> {
  /** Current value — defaultValue until the first settled read, then the cached snapshot. */
  value: T
  /** Persists `newValue` to TG CloudStorage. Per-call callbacks override the hook's default error toast. */
  setValue: (newValue: T, callbacks?: StorageOperationCallbacks) => Promise<void>
  /** Removes the key from TG CloudStorage. Subscribers re-render to defaultValue. */
  deleteValue: (callbacks?: StorageOperationCallbacks) => Promise<void>
  /** Imperatively re-reads the current value from the controller (cache-first; falls back to defaultValue on TG failure). */
  getValue: (callbacks?: StorageOperationCallbacks) => Promise<T>
  /**
   * Bulk reads (key-agnostic — `keys` can include keys outside the hook's
   * own `key`). Cache-first per key, single TG round-trip for the missing
   * subset. Use cautiously: the operation is not scoped to this hook.
   *
   * On TG failure for the missing subset the call resolves to every requested
   * key mapped to `null` — cached values are NOT leaked in a partial result
   * (callers expect all-keys-or-nothing). Cached entries stay intact and can
   * still be read via individual `getValue` calls.
   */
  getValues: <V = unknown>(keys: string[], callbacks?: StorageOperationCallbacks) => Promise<Record<string, V | null>>
  /**
   * Bulk write (key-agnostic). Composed from parallel per-key setItem calls;
   * partial-failure semantics — see controller's setItems docstring.
   */
  setValues: (entries: Record<string, unknown>, callbacks?: StorageOperationCallbacks) => Promise<void>
  /** Bulk delete (key-agnostic). All-or-nothing per TG semantics. */
  deleteValues: (keys: string[], callbacks?: StorageOperationCallbacks) => Promise<void>
  /** Lists every key the user has stored in TG CloudStorage (NOT scoped to this hook). Returns `null` on failure — distinguishable from an empty storage `[]`. */
  getKeys: (callbacks?: StorageOperationCallbacks) => Promise<string[] | null>
  /** Wipes every key the user has stored (NOT scoped to this hook — use sparingly). */
  clear: (callbacks?: StorageOperationCallbacks) => Promise<void>
}

/**
 * Subscribes a component to a single TG CloudStorage key. Delegates all I/O,
 * caching, and subscriber notification to the shared `cloudStorageController`:
 * the hook is purely a React adapter around the controller's
 * subscribe/getSnapshot pair plus a first-read bootstrap.
 *
 * On mount the hook seeds the key from TG once per session — if the cloud
 * doesn't have a value yet, it writes `defaultValue` so the cloud and the
 * local snapshot stay aligned (so future getValue calls don't return null
 * from a different component). The bootstrap is a no-op when the controller
 * has already settled the key (another consumer of the same key already
 * populated the cache).
 *
 * Per-call callbacks (`setValue(value, { onSuccess, onError })`) let
 * individual call sites react to the outcome — for example, show an error
 * toast on the rare `DATA_TOO_LONG`/`SAVE_FAILED` paths. The controller
 * still logs errors internally regardless of whether a callback is supplied.
 */
export const useTelegramCloudStorage = <T>({
  key,
  defaultValue
}: UseTelegramCloudStorageParams<T>): UseTelegramCloudStorageResult<T> => {
  const toast = useToast()
  const { t } = useTranslation()

  // Default error handler — resolves error.code through the constants table
  // to its FTL key + English fallback, then routes through useToast.showError
  // so user-callable operations surface failures to the user. Bootstrap passes
  // only a silent onError flag and avoids the default toast handler.
  // Per-call `onError` overrides this default when supplied.
  const defaultOnError = useEventCallback((error: ResolvedStorageError) => {
    const entry = getStorageErrorMessage(error.code)
    toast.showError(null, entry ? t(`telegram:${entry.key}`, entry.fallback) : error.message)
  })

  const withDefaultError = useEventCallback(
    (callbacks?: StorageOperationCallbacks): StorageOperationCallbacks => ({
      onSuccess: callbacks?.onSuccess,
      onError: callbacks?.onError ?? defaultOnError
    })
  )

  const resetVersion = useSyncExternalStore(
    callback => cloudStorageController.subscribeToReset(callback),
    () => cloudStorageController.getResetSnapshot(),
    () => 0
  )

  useEffect(() => {
    // Controller owns the "settled keys" set — querying it directly avoids
    // a stale-local-state bug (if `key` changes between renders, a local
    // useState `isInitialized` would still reflect the OLD key) and skips
    // the re-render that flipping local state would otherwise force after
    // bootstrap. The subscriber re-renders anyway when getItem/setItem
    // mutate the snapshot and emit.
    //
    // Bootstrap is intentionally silent on failure — the controller logs to
    // console.error via logStorageError, but we don't surface a toast for
    // auto-triggered reads the user didn't initiate. Only user-callable
    // methods (setValue/getValue/etc.) get the default toast handler.
    if (cloudStorageController.isInitialized(key)) return
    void (async () => {
      // DESERIALIZATION_ERROR is a self-heal trigger: the stored value is unparseable
      // (schema drift, partial write), so we overwrite the cloud slot with `defaultValue`
      // instead of leaving the app stuck on corrupt JSON. Real read failures
      // (network/permission) keep `readFailed = true` so we do NOT touch the cloud.
      let readFailed = false
      const stored = await cloudStorageController.getItem<T>(key, {
        onError: error => {
          readFailed = error.code !== STORAGE_ERROR_CODE.DESERIALIZATION_ERROR
        }
      })
      if (cloudStorageController.getResetSnapshot() !== resetVersion) return
      if (!readFailed && stored === null) {
        await cloudStorageController.setItem(key, defaultValue)
      }
    })()
  }, [key, defaultValue, resetVersion])

  const value = useSyncExternalStore<T>(
    callback => cloudStorageController.subscribe(key, callback),
    () => (cloudStorageController.getSnapshot(key) as T | null) ?? defaultValue,
    () => defaultValue
  )

  const setValue = useEventCallback(async (newValue: T, callbacks?: StorageOperationCallbacks): Promise<void> => {
    await cloudStorageController.setItem(key, newValue, withDefaultError(callbacks))
  })

  const deleteValue = useEventCallback(async (callbacks?: StorageOperationCallbacks): Promise<void> => {
    await cloudStorageController.deleteItem(key, withDefaultError(callbacks))
  })

  const getValue = useEventCallback(async (callbacks?: StorageOperationCallbacks): Promise<T> => {
    const stored = await cloudStorageController.getItem<T>(key, withDefaultError(callbacks))
    return stored ?? defaultValue
  })

  const getValues = useCallback(
    <V = unknown>(keys: string[], callbacks?: StorageOperationCallbacks): Promise<Record<string, V | null>> =>
      cloudStorageController.getItems<V>(keys, withDefaultError(callbacks)),
    [withDefaultError]
  )

  const setValues = useEventCallback(
    (entries: Record<string, unknown>, callbacks?: StorageOperationCallbacks): Promise<void> =>
      cloudStorageController.setItems(entries, withDefaultError(callbacks))
  )

  const deleteValues = useEventCallback(
    (keys: string[], callbacks?: StorageOperationCallbacks): Promise<void> =>
      cloudStorageController.deleteItems(keys, withDefaultError(callbacks))
  )

  const getKeys = useEventCallback(
    (callbacks?: StorageOperationCallbacks): Promise<string[] | null> =>
      cloudStorageController.getKeys(withDefaultError(callbacks))
  )

  const clear = useEventCallback(
    (callbacks?: StorageOperationCallbacks): Promise<void> => cloudStorageController.clear(withDefaultError(callbacks))
  )

  return useMemo(
    () => ({ value, setValue, deleteValue, getValue, getValues, setValues, deleteValues, getKeys, clear }),
    [value, setValue, deleteValue, getValue, getValues, setValues, deleteValues, getKeys, clear]
  )
}
